import {
  MonthlyInterval,
  MonthlySchedule,
  MonthlyTrigger,
  RepeatType,
  TaskScheduleValue,
  WeekdayOrdinal,
} from '../Components/CalendarSchedulerTypes';
import { DayKey, Schedule, ScheduleInterval } from '../Components/WorkSchedulerTypes';
import { addDaysUTC, endOfMonthUTC, isSameDayUTC, MONTHS_SHORT, utcOf } from './utcDate';

export const WEEKDAYS: { key: DayKey; short: string; label: string }[] = [
  { key: 'sun', short: 'Su', label: 'Sunday' },
  { key: 'mon', short: 'Mo', label: 'Monday' },
  { key: 'tue', short: 'Tu', label: 'Tuesday' },
  { key: 'wed', short: 'We', label: 'Wednesday' },
  { key: 'thu', short: 'Th', label: 'Thursday' },
  { key: 'fri', short: 'Fr', label: 'Friday' },
  { key: 'sat', short: 'Sa', label: 'Saturday' },
];

export const ORDINALS: { value: WeekdayOrdinal; label: string; week: number | null }[] = [
  { value: 'first', label: 'First', week: 1 },
  { value: 'second', label: 'Second', week: 2 },
  { value: 'third', label: 'Third', week: 3 },
  { value: 'fourth', label: 'Fourth', week: 4 },
  { value: 'last', label: 'Last', week: null },
];

export const DEFAULT_TIME = '09:00';

export const quarterMonths = (quarterMonth: number): string[] =>
  [0, 3, 6, 9].map((q) => MONTHS_SHORT[q + quarterMonth - 1]);

const RENAMED_TIMEZONES: Record<string, string> = {
  'Europe/Kiev': 'Europe/Kyiv',
  'Europe/Uzhgorod': 'Europe/Kyiv',
  'Europe/Zaporozhye': 'Europe/Kyiv',
};

export const browserTimezone = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    return RENAMED_TIMEZONES[timezone] ?? timezone;
  } catch {
    return 'UTC';
  }
};

export const isMonthlySchedule = (
  schedule: TaskScheduleValue | null | undefined,
): schedule is MonthlySchedule =>
  !!schedule &&
  ((schedule as MonthlySchedule).repeat === 'monthly' ||
    (schedule as MonthlySchedule).repeat === 'quarterly');

export const getRepeat = (schedule: TaskScheduleValue | null | undefined): RepeatType =>
  isMonthlySchedule(schedule) ? schedule.repeat : 'weekly';

export const scheduleSlots = (
  schedule: TaskScheduleValue,
): Partial<Record<string, ScheduleInterval[]>> =>
  isMonthlySchedule(schedule) ? schedule.slots : schedule;

export const hasScheduleSlots = (schedule: TaskScheduleValue): boolean =>
  Object.keys(scheduleSlots(schedule)).length > 0;

const defaultSlotsForTrigger = (trigger: MonthlyTrigger): Record<string, ScheduleInterval[]> => {
  const key = trigger === 'date' ? '1' : trigger === 'weekday' ? 'mon' : 'first';
  return { [key]: [{ start: DEFAULT_TIME }] };
};

const newMonthlySchedule = (
  repeat: MonthlySchedule['repeat'],
  trigger: MonthlyTrigger,
): MonthlySchedule => ({
  repeat,
  trigger,
  ...(repeat === 'quarterly' ? { quarter_month: 1 as const } : {}),
  ...(trigger === 'date' ? { missing_day: 'last_day' as const } : {}),
  ...(trigger === 'weekday' ? { ordinal: 'first' as const } : {}),
  slots: defaultSlotsForTrigger(trigger),
});

export const switchRepeat = (
  schedule: TaskScheduleValue,
  repeat: RepeatType,
): TaskScheduleValue => {
  if (repeat === getRepeat(schedule)) return schedule;
  if (repeat === 'weekly') return {};
  return newMonthlySchedule(repeat, 'date');
};

export const switchTrigger = (
  schedule: MonthlySchedule,
  trigger: MonthlyTrigger,
): MonthlySchedule => {
  if (trigger === schedule.trigger) return schedule;
  const next = newMonthlySchedule(schedule.repeat, trigger);
  return schedule.quarter_month ? { ...next, quarter_month: schedule.quarter_month } : next;
};

// ============================================================
// Labels
// ============================================================

const ordinalSuffix = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

export const orderedSlotKeys = (schedule: MonthlySchedule): string[] => {
  const keys = Object.keys(schedule.slots);
  if (schedule.trigger === 'date') {
    const rank = (k: string) => (k === 'last' ? 32 : Number(k));
    return keys.sort((a, b) => rank(a) - rank(b));
  }
  if (schedule.trigger === 'weekday') {
    const order = WEEKDAYS.map((d) => d.key as string);
    return keys.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }
  return ['first', 'last'].filter((k) => keys.includes(k));
};

export const slotLabel = (schedule: MonthlySchedule, key: string): string => {
  if (schedule.trigger === 'date') return key === 'last' ? 'Last day' : ordinalSuffix(Number(key));
  if (schedule.trigger === 'weekday') {
    const ordinal = ORDINALS.find((o) => o.value === (schedule.ordinal ?? 'first'))?.label ?? '';
    return `${ordinal} ${WEEKDAYS.find((d) => d.key === key)?.label ?? key}`;
  }
  return key === 'first' ? 'First business day' : 'Last business day';
};

const joinWords = (words: string[]): string =>
  words.length <= 1
    ? words.join('')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

const timesOf = (intervals: ScheduleInterval[] | undefined) =>
  joinWords([...new Set((intervals ?? []).map((i) => i.start))].sort());

export const describeSchedule = (schedule: TaskScheduleValue): string => {
  let entries: { label: string; times: string }[];
  let prefix: string;
  let suffix = '';

  if (isMonthlySchedule(schedule)) {
    entries = orderedSlotKeys(schedule).map((key) => ({
      label: lowerFirst(slotLabel(schedule, key)),
      times: timesOf(schedule.slots[key]),
    }));
    prefix = 'Runs on the ';
    if (schedule.repeat === 'quarterly') {
      suffix = ` of ${joinWords(quarterMonths(schedule.quarter_month ?? 1))}`;
    } else {
      suffix = ' of every month';
    }
  } else {
    const days = scheduleSlots(schedule);
    entries = WEEKDAYS.filter((d) => days[d.key]?.length).map((d) => ({
      label: d.label,
      times: timesOf(days[d.key]),
    }));
    prefix = 'Runs every ';
  }

  if (!entries.length) return 'No trigger selected';

  const sameTimes = entries.every((e) => e.times === entries[0].times);
  if (sameTimes) {
    return `${prefix}${joinWords(entries.map((e) => e.label))}${suffix} at ${entries[0].times}`;
  }
  return `${prefix}${joinWords(entries.map((e) => `${e.label} at ${e.times}`))}${suffix}`;
};

// ============================================================
// Next runs
// ============================================================

const isWeekend = (d: Date) => d.getUTCDay() === 0 || d.getUTCDay() === 6;

const businessDay = (day: Date, edge: 'first' | 'last'): number => {
  let d = edge === 'first' ? utcOf(day.getUTCFullYear(), day.getUTCMonth(), 1) : endOfMonthUTC(day);
  while (isWeekend(d)) d = addDaysUTC(d, edge === 'first' ? 1 : -1);
  return d.getUTCDate();
};

const matchingSlotKeys = (schedule: TaskScheduleValue, day: Date): string[] => {
  const d = day.getUTCDate();
  const weekdayKey = WEEKDAYS[day.getUTCDay()].key;

  if (!isMonthlySchedule(schedule)) {
    return scheduleSlots(schedule)[weekdayKey]?.length ? [weekdayKey] : [];
  }

  if (
    schedule.repeat === 'quarterly' &&
    day.getUTCMonth() % 3 !== (schedule.quarter_month ?? 1) - 1
  ) {
    return [];
  }

  const last = endOfMonthUTC(day).getUTCDate();
  const keys = Object.keys(schedule.slots);

  if (schedule.trigger === 'date') {
    const runOnLast = (schedule.missing_day ?? 'last_day') !== 'skip';
    return keys.filter((k) =>
      k === 'last' ? d === last : Number(k) === d || (runOnLast && Number(k) > last && d === last),
    );
  }

  if (schedule.trigger === 'weekday') {
    if (!keys.includes(weekdayKey)) return [];
    const week = ORDINALS.find((o) => o.value === (schedule.ordinal ?? 'first'))?.week;
    const matches = week === null ? d + 7 > last : Math.floor((d - 1) / 7) + 1 === week;
    return matches ? [weekdayKey] : [];
  }

  return keys.filter((k) => (k === 'first' || k === 'last') && businessDay(day, k) === d);
};

const nowLocal = (): { today: Date; minutes: number } => {
  const now = new Date();
  return {
    today: utcOf(now.getFullYear(), now.getMonth(), now.getDate()),
    minutes: now.getHours() * 60 + now.getMinutes(),
  };
};

const parseFormDate = (value?: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? utcOf(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const nextRunDates = (
  schedule: TaskScheduleValue,
  count = 3,
  startDate?: string,
  endDate?: string,
): Date[] => {
  const { today, minutes } = nowLocal();
  const start = parseFormDate(startDate);
  const end = parseFormDate(endDate);
  const slots = scheduleSlots(schedule);

  const result: Date[] = [];
  let day = start && start > today ? start : today;

  for (let i = 0; i < 800 && result.length < count; i++) {
    if (end && day > end) break;
    const isToday = isSameDayUTC(day, today);
    const due = matchingSlotKeys(schedule, day).some((key) =>
      (slots[key] ?? []).some((s) => !isToday || toMinutes(s.start) > minutes),
    );
    if (due) result.push(day);
    day = addDaysUTC(day, 1);
  }

  return result;
};

const RUN_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

export const formatRunDate = (date: Date): string => {
  const label = RUN_DATE_FORMAT.format(date);
  return date.getUTCFullYear() === new Date().getFullYear()
    ? label
    : `${label} ${date.getUTCFullYear()}`;
};

// ============================================================
// Local time <-> UTC
// ============================================================

const minutesToTime = (total: number) => {
  const normalized = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}`;
};

type SlotMap = Partial<Record<string, MonthlyInterval[]>>;

const mapSlots = (slots: SlotMap, map: (interval: MonthlyInterval) => MonthlyInterval) =>
  Object.fromEntries(
    Object.entries(slots).map(([key, intervals]) => [key, (intervals ?? []).map(map)]),
  );

const toUtcInterval = (
  { day_offset: _ignored, ...interval }: MonthlyInterval,
  offset: number,
): MonthlyInterval => {
  const total = toMinutes(interval.start) + offset;
  const dayOffset = Math.floor(total / 1440);
  return {
    ...interval,
    start: minutesToTime(total),
    ...(interval.end ? { end: minutesToTime(toMinutes(interval.end) + offset) } : {}),
    ...(dayOffset ? { day_offset: dayOffset } : {}),
  };
};

const toLocalMinutes = ({ start, day_offset: dayOffset = 0 }: MonthlyInterval, offset: number) =>
  toMinutes(start) + dayOffset * 1440 - offset;

export const toUtcSchedule = (schedule: TaskScheduleValue): TaskScheduleValue => {
  const offset = new Date().getTimezoneOffset();
  const toUtc = (interval: MonthlyInterval) => toUtcInterval(interval, offset);
  return isMonthlySchedule(schedule)
    ? { ...schedule, slots: mapSlots(schedule.slots, toUtc) }
    : (mapSlots(schedule, toUtc) as Schedule);
};

export const toLocalSchedule = (schedule: TaskScheduleValue): TaskScheduleValue => {
  const offset = new Date().getTimezoneOffset();

  if (isMonthlySchedule(schedule)) {
    return {
      ...schedule,
      slots: mapSlots(schedule.slots, ({ day_offset: _ignored, ...interval }) => ({
        ...interval,
        start: minutesToTime(toLocalMinutes(interval, offset)),
      })),
    };
  }

  const result: Schedule = {};
  Object.entries(schedule as SlotMap).forEach(([key, intervals]) => {
    const dayIndex = WEEKDAYS.findIndex((d) => d.key === key);
    if (dayIndex === -1) return;
    (intervals ?? []).forEach(({ day_offset: dayOffset, ...interval }) => {
      const total = toLocalMinutes({ ...interval, day_offset: dayOffset }, offset);
      const day = WEEKDAYS[(((dayIndex + Math.floor(total / 1440)) % 7) + 7) % 7].key;
      const end = interval.end ? { end: minutesToTime(toMinutes(interval.end) - offset) } : {};
      result[day] = [...(result[day] ?? []), { start: minutesToTime(total), ...end }];
    });
  });
  return result;
};
